import { useAtomValue } from 'jotai';
import { usePlatform } from '@localagent/platform';
import { chatSessionAtom } from '@localagent/sdk/react';
import type { WorkspaceProjection } from '@localagent/sdk';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import { useAttachWorkspaceToChat } from '../../hooks/useAttachWorkspaceToChat';

export function isAttachableWorkspace(workspace: WorkspaceProjection): boolean {
  return workspace.lifecycle === 'active' && workspace.capabilities.content_readable;
}

/**
 * Optional inline "attach a workspace" call-out shown on a New Chat draft.
 * Attaching a workspace is a first action: `ChatSession.attachWorkspace` persists immediately.
 */
export function WorkspaceAttachCallout() {
  const platform = usePlatform();
  const session = useAtomValue(chatSessionAtom);
  const { attach, attaching } = useAttachWorkspaceToChat();

  const { data: workspaces } = useWorkspaces();
  const attachable = (workspaces ?? []).filter(isAttachableWorkspace);

  if (platform.type !== 'desktop') return null;
  if (!session?.isDraft || session.workspaceId) return null;
  if (attachable.length === 0) return null;

  return (
    <div
      data-testid="workspace-attach-callout"
      className="mx-auto mb-2 flex w-full max-w-2xl items-center gap-2 rounded-lg bg-card px-3 py-2 text-body"
    >
      <span className="text-muted-foreground">Attach a workspace for file &amp; git tools:</span>
      <select
        data-testid="workspace-attach-select"
        defaultValue=""
        disabled={attaching}
        onChange={(e) => attach(e.target.value)}
        className="rounded-md bg-muted px-2 py-1 text-body focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <option value="" disabled>
          {attaching ? 'Attaching…' : 'Select a workspace…'}
        </option>
        {attachable.map((ws) => (
          <option key={ws.workspace_id} value={ws.workspace_id}>
            {ws.name}
          </option>
        ))}
      </select>
    </div>
  );
}
